import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createAnnouncement,
  deleteAnnouncement,
  fetchAnnouncements,
  updateAnnouncement
} from '../../api/announcements';
import { getErrorMessage } from '../../api/client';
import { ANNOUNCEMENT_REPEAT_OPTIONS, announcementRepeatLabel } from '../../utils/announcementRepeat';

function todayIso() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Manila' });
}

function emptyForm() {
  const today = todayIso();
  return {
    title: '',
    message: '',
    eventDate: today,
    endDate: today,
    repeat: 'none',
    repeatUntil: '',
    storeId: '',
    active: true
  };
}

function getStoreName(stores, storeId) {
  if (!storeId) return 'All stores';
  const id = typeof storeId === 'object' ? storeId._id || storeId.id : storeId;
  const fromList = stores.find((store) => (store._id || store.id) === id);
  if (fromList) return fromList.name;
  if (typeof storeId === 'object' && storeId.name) return storeId.name;
  return 'Selected store';
}

function formatDateLabel(value) {
  if (!value) return '-';
  const [year, month, day] = String(value).split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-PH', {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  });
}

function occurrenceStart(item) {
  return item.occurrenceDate || item.eventDate;
}

function occurrenceEnd(item) {
  return item.occurrenceEndDate || item.endDate || occurrenceStart(item);
}

function byOccurrenceDate(a, b) {
  return occurrenceStart(a).localeCompare(occurrenceStart(b));
}

export default function AnnouncementsManager({ authConfig, onUnauthorized, stores = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAnnouncements(authConfig, {
        includeInactive: showInactive ? 'true' : 'false'
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(getErrorMessage(err, 'Failed to load announcements.'));
    } finally {
      setLoading(false);
    }
  }, [authConfig, onUnauthorized, showInactive]);

  useEffect(() => {
    load();
  }, [load]);

  const today = todayIso();
  const grouped = useMemo(() => {
    const activeItems = items.filter((item) => item.active !== false);
    return {
      today: activeItems.filter((item) => occurrenceStart(item) <= today && occurrenceEnd(item) >= today).sort(byOccurrenceDate),
      upcoming: activeItems.filter((item) => occurrenceStart(item) > today).sort(byOccurrenceDate),
      past: activeItems.filter((item) => occurrenceEnd(item) < today).sort((a, b) => byOccurrenceDate(b, a)),
      inactive: items.filter((item) => item.active === false)
    };
  }, [items, today]);

  const openAdd = () => {
    setError('');
    setEditingId(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (item) => {
    setError('');
    setEditingId(item._id || item.id);
    setForm({
      title: item.title || '',
      message: item.message || '',
      eventDate: item.eventDate || todayIso(),
      endDate: item.endDate || item.eventDate || todayIso(),
      repeat: item.repeat || 'none',
      repeatUntil: item.repeatUntil || '',
      storeId: typeof item.storeId === 'object' ? item.storeId?._id || item.storeId?.id || '' : item.storeId || '',
      active: item.active !== false
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setError('');
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!form.eventDate) {
      setError('Event date is required.');
      return;
    }
    if (form.endDate && form.endDate < form.eventDate) {
      setError('End date cannot be before event date.');
      return;
    }
    if (form.repeat !== 'none' && form.repeatUntil && form.repeatUntil < form.eventDate) {
      setError('Repeat until cannot be before event date.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: form.title.trim(),
        message: form.message.trim(),
        eventDate: form.eventDate,
        endDate: form.endDate || form.eventDate,
        repeat: form.repeat,
        repeatUntil: form.repeat !== 'none' ? form.repeatUntil || null : null,
        storeId: form.storeId || null,
        active: form.active
      };
      if (editingId) await updateAnnouncement(editingId, payload, authConfig);
      else await createAnnouncement(payload, authConfig);
      closeModal();
      await load();
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(getErrorMessage(err, 'Failed to save announcement.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    const id = item._id || item.id;
    const repeatWarning = item.repeat && item.repeat !== 'none' ? ' This deletes the entire repeating series.' : '';
    if (!id || !window.confirm(`Delete "${item.title}"?${repeatWarning}`)) return;
    setError('');
    try {
      await deleteAnnouncement(id, authConfig);
      await load();
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(getErrorMessage(err, 'Failed to delete announcement.'));
    }
  };

  const renderCard = (item) => {
    const eventDate = occurrenceStart(item);
    const endDate = occurrenceEnd(item);
    const repeatLabel = announcementRepeatLabel(item.repeat);
    const isToday = eventDate <= today && endDate >= today;
    const isPast = endDate < today;
    return (
      <article key={item._id || item.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                item.active === false
                  ? 'bg-gray-100 text-gray-600'
                  : isToday
                    ? 'bg-green-100 text-green-700'
                    : isPast
                      ? 'bg-slate-100 text-slate-600'
                      : 'bg-indigo-100 text-indigo-700'
              }`}>
                {item.active === false ? 'Inactive' : isToday ? 'Today' : isPast ? 'Past' : 'Upcoming'}
              </span>
              <span className="text-xs text-gray-500">{getStoreName(stores, item.storeId)}</span>
            </div>
            <h4 className="truncate text-base font-semibold text-gray-900">{item.title}</h4>
            <p className="mt-1 text-sm font-medium text-gray-600">
              {formatDateLabel(eventDate)}
              {endDate && endDate !== eventDate ? ` to ${formatDateLabel(endDate)}` : ''}
            </p>
            {repeatLabel ? (
              <p className="mt-1 text-xs text-indigo-700">
                Repeats: {repeatLabel}{item.repeatUntil ? ` · Until ${formatDateLabel(item.repeatUntil)}` : ''}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 gap-2">
            <button type="button" onClick={() => openEdit(item)} className="rounded border px-3 py-1.5 text-sm hover:bg-gray-50">Edit</button>
            <button type="button" onClick={() => handleDelete(item)} className="rounded bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700">Delete</button>
          </div>
        </div>
        {item.message ? (
          <p className="mt-3 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-700">{item.message}</p>
        ) : null}
      </article>
    );
  };

  return (
    <div className="space-y-5">
      {error && !showModal ? <p role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p> : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="font-medium">Calendar announcements</h4>
          <p className="text-xs text-gray-500">Schedule notices employees see when they open the employee portal.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(event) => setShowInactive(event.target.checked)}
            />
            Show inactive
          </label>
          <button type="button" onClick={load} className="rounded-lg border px-3 py-2 text-sm hover:bg-gray-50">Refresh</button>
          <button type="button" onClick={openAdd} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500">
            Add announcement
          </button>
        </div>
      </div>

      {loading ? (
        <p className="rounded border bg-white p-4 text-sm text-gray-500">Loading announcements...</p>
      ) : items.length === 0 ? (
        <p className="rounded border bg-white p-4 text-sm text-gray-500">No announcements scheduled.</p>
      ) : (
        <div className="space-y-6">
          <section>
            <h5 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">Today</h5>
            <div className="grid gap-3 xl:grid-cols-2">
              {grouped.today.length ? grouped.today.map(renderCard) : (
                <p className="rounded border bg-white p-4 text-sm text-gray-500">No event for today.</p>
              )}
            </div>
          </section>

          <section>
            <h5 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">Upcoming</h5>
            <div className="grid gap-3 xl:grid-cols-2">
              {grouped.upcoming.length ? grouped.upcoming.map(renderCard) : (
                <p className="rounded border bg-white p-4 text-sm text-gray-500">No upcoming events.</p>
              )}
            </div>
          </section>

          {grouped.past.length ? (
            <section>
              <h5 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">Past</h5>
              <div className="grid gap-3 xl:grid-cols-2">
                {grouped.past.map(renderCard)}
              </div>
            </section>
          ) : null}

          {grouped.inactive.length ? (
            <section>
              <h5 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">Inactive</h5>
              <div className="grid gap-3 xl:grid-cols-2">
                {grouped.inactive.map(renderCard)}
              </div>
            </section>
          ) : null}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="announcement-form-title">
          <div className="flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex shrink-0 items-center justify-between border-b px-5 py-4">
              <h4 id="announcement-form-title" className="font-semibold text-gray-900">
                {editingId ? 'Edit announcement' : 'Add announcement'}
              </h4>
              <button type="button" onClick={closeModal} className="text-2xl leading-none text-gray-400 hover:text-gray-700" aria-label="Close announcement form">x</button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto p-5">
              {error ? <p role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p> : null}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="announcementTitle">Title</label>
                <input
                  id="announcementTitle"
                  type="text"
                  value={form.title}
                  onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                  className="w-full rounded border px-3 py-2"
                  placeholder="e.g. Team meeting, payroll day, store cleaning"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="announcementMessage">Message</label>
                <textarea
                  id="announcementMessage"
                  value={form.message}
                  onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))}
                  rows={4}
                  className="w-full rounded border px-3 py-2"
                  placeholder="Details employees should read when they log in"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="announcementDate">Event date</label>
                  <input
                    id="announcementDate"
                    type="date"
                    value={form.eventDate}
                    onChange={(event) => {
                      const eventDate = event.target.value;
                      setForm((prev) => ({
                        ...prev,
                        eventDate,
                        endDate: !prev.endDate || prev.endDate === prev.eventDate ? eventDate : prev.endDate
                      }));
                    }}
                    className="w-full rounded border px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="announcementEndDate">End date</label>
                  <input
                    id="announcementEndDate"
                    type="date"
                    value={form.endDate}
                    min={form.eventDate}
                    onChange={(event) => setForm((prev) => ({ ...prev, endDate: event.target.value }))}
                    className="w-full rounded border px-3 py-2"
                  />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="announcementRepeat">Repeat</label>
                  <select
                    id="announcementRepeat"
                    value={form.repeat}
                    onChange={(event) => {
                      const repeat = event.target.value;
                      setForm((prev) => ({ ...prev, repeat, repeatUntil: repeat === 'none' ? '' : prev.repeatUntil }));
                    }}
                    className="w-full rounded border px-3 py-2"
                  >
                    {ANNOUNCEMENT_REPEAT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                {form.repeat !== 'none' ? (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="announcementRepeatUntil">Repeat until (optional)</label>
                    <input
                      id="announcementRepeatUntil"
                      type="date"
                      min={form.eventDate}
                      value={form.repeatUntil}
                      onChange={(event) => setForm((prev) => ({ ...prev, repeatUntil: event.target.value }))}
                      className="w-full rounded border px-3 py-2"
                      aria-describedby="announcementRepeatHelp"
                    />
                  </div>
                ) : null}
              </div>
              {form.repeat !== 'none' ? (
                <div id="announcementRepeatHelp" className="space-y-1 text-xs text-gray-500">
                  <p>Event date and end date set the first occurrence and how many days each occurrence lasts.</p>
                  <p>Leave Repeat until blank to continue indefinitely. No new occurrence starts after that date.</p>
                  {['monthly', 'quarterly', 'semiannual', 'yearly'].includes(form.repeat) ? (
                    <p>If the day does not exist in a month, it uses that month's last day.</p>
                  ) : null}
                  {editingId ? <p>Changes apply to the entire repeating series.</p> : null}
                </div>
              ) : null}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="announcementStore">Store</label>
                <select
                  id="announcementStore"
                  value={form.storeId}
                  onChange={(event) => setForm((prev) => ({ ...prev, storeId: event.target.value }))}
                  className="w-full rounded border px-3 py-2"
                >
                  <option value="">All stores</option>
                  {stores.map((store) => (
                    <option key={store._id || store.id} value={store._id || store.id}>{store.name}</option>
                  ))}
                </select>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(event) => setForm((prev) => ({ ...prev, active: event.target.checked }))}
                />
                Active
              </label>
              <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
                <button type="button" onClick={closeModal} className="rounded border px-4 py-2 text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-60">
                  {saving ? 'Saving...' : editingId ? 'Update announcement' : 'Save announcement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
